import { useMemo } from 'react';
import { useInfiniteQuery, type QueryClient } from '@tanstack/react-query';
import {
  HISTORY_FIRST_PAGE,
  HISTORY_PAGE_SIZE,
  type HistoryPage,
} from '@visious/core';
import { AUTH_QUERY_KEY, type AuthApiProblem } from '../api/auth';
import { fetchHistoryPage } from '../api/history';
import { getPlatform } from '../platform/instance';
import { useSession } from '../platform/session';

/**
 * The id list, as a query — shared between the History screen (which renders it) and the mutations
 * (which invalidate it), which is why it is here rather than under `screens/history/`.
 *
 * **`useInfiniteQuery`, for `useSubscriptions`'s reason**: one list, one identity, append-only from the
 * reader's point of view, and the cursor is a page number the previous page determines. `retry: false`
 * because §3's retry and backoff live in `createApiTransport`.
 *
 * **Keyed under `AUTH_QUERY_KEY`** — 3a's convention with teeth, and it matters more here than anywhere
 * else in the app: a watch history left in the query cache after sign-out is the one piece of state whose
 * lingering is a genuine privacy problem rather than a stale render.
 */

export type HistoryPageResult =
  | { readonly ok: true; readonly page: HistoryPage }
  | { readonly ok: false; readonly problem: AuthApiProblem };

/**
 * The window size used to be part of this key, because it was a setting and two sizes are two
 * different pagings of one list. It is `HISTORY_PAGE_SIZE` for everybody now that the rows-per-page
 * setting is retired — which also retires a mismatch this key could produce: the two
 * invalidators below called it without a size and therefore built a key ending in the *default* 40,
 * so on a reader whose account said 10 they invalidated nothing.
 */
export function historyKey(grantKey: number): readonly unknown[] {
  return [AUTH_QUERY_KEY, 'history', grantKey];
}

/**
 * `null` scopes, deliberately — the same call the sign-in page and the Subscriptions page make, for the
 * reason `useSubscriptions` gives: a page that pre-refuses its own request reports a scope gap it
 * *inferred* rather than one that happened. The reads are where the instance is the authority; the
 * writes pass their grant, because there a wrong guess is unanswerable rather than one wasted request.
 */
async function loadPage(page: number, pageSize: number): Promise<HistoryPageResult> {
  const boot = await getPlatform();
  if (!boot.ok) return { ok: false, problem: { kind: 'bad-url', path: '/api/v1/auth/history' } };

  const result = await fetchHistoryPage(boot.platform, page, pageSize, null);
  return result.ok ? { ok: true, page: result.value } : { ok: false, problem: result.error };
}

export interface HistoryListQuery {
  readonly pages: readonly HistoryPage[];
  readonly problem: AuthApiProblem | null;
  readonly pending: boolean;
  readonly fetchingNextPage: boolean;
  readonly refreshing: boolean;
  readonly updatedAtMs: number;
  readonly fetchNextPage: () => void;
  readonly refetch: () => void;
}

export function useHistoryList(): HistoryListQuery {
  const session = useSession();
  // The grant's own timestamp is part of the identity: a failure is a *value* here rather than a thrown
  // error, so a refusal caused by the previous token would otherwise be served to the new one.
  const grantKey = session.grantedAtEpochSec ?? 0;

  const query = useInfiniteQuery({
    queryKey: historyKey(grantKey),
    initialPageParam: HISTORY_FIRST_PAGE,
    // Always sent explicitly, because an omitted `max_results` is sized by the *account's* own
    // preference rather than by a protocol default (§8c) — and a full page read as short ends the list.
    queryFn: ({ pageParam }) => loadPage(pageParam, HISTORY_PAGE_SIZE),
    getNextPageParam: (_last, all) => all.length + HISTORY_FIRST_PAGE,
    retry: false,
    /*
     * Short, unlike the subscription feed's five minutes. A history changes because *the reader* changed
     * it — every video they watch prepends a row — so the list going stale is the normal case rather
     * than an event upstream. 30 seconds is enough that opening a video from the history and pressing
     * Back does not re-read it, and short enough that the row they just watched is there when they
     * return.
     */
    staleTime: 30 * 1000,
    enabled: session.ready && session.facts.signedIn,
  });

  const pages = useMemo(
    () => (query.data?.pages ?? []).flatMap((result) => (result.ok ? [result.page] : [])),
    [query.data?.pages],
  );

  return {
    pages,
    problem: firstProblem(query.data?.pages, query.error),
    pending: query.isPending,
    fetchingNextPage: query.isFetchingNextPage,
    refreshing: query.isFetching && !query.isPending && !query.isFetchingNextPage,
    updatedAtMs: query.dataUpdatedAt,
    fetchNextPage: () => {
      if (!query.isFetchingNextPage) void query.fetchNextPage();
    },
    refetch: () => void query.refetch(),
  };
}

const NETWORK_PROBLEM: AuthApiProblem = {
  kind: 'transport',
  error: { kind: 'network', status: null, url: '', retryable: true },
};

/**
 * The first page that failed, or `null`. A *later* page failing is treated as a page problem rather than
 * as a load-more error, for `useSubscriptions`'s reason: the alternative leaves the rows on screen next
 * to a silent button that does nothing.
 */
function firstProblem(
  pages: readonly HistoryPageResult[] | undefined,
  error: unknown,
): AuthApiProblem | null {
  if (error !== null && error !== undefined) return NETWORK_PROBLEM;
  for (const page of pages ?? []) if (!page.ok) return page.problem;
  return null;
}

/**
 * Re-read the list. Used after a removal and after a clear.
 *
 * **A full refetch rather than editing the cached pages**, which is the opposite of what
 * `noteEntryAdded` does for playlists — and the reason is `nextHistoryPage`. Removing a row locally
 * would turn the last loaded page from 40 rows into 39, which is precisely the evidence the pager reads
 * as "this is the last page": one removal would end the list four hundred rows early. Refetching every
 * loaded page costs ~600 bytes each and gives windows that are consistent with each other, so the fold's
 * duplicate count stays honest too (a stale page beside a fresh one is what produces a spurious "the
 * list moved" note).
 */
export function invalidateHistory(client: QueryClient, grantKey: number): void {
  void client.invalidateQueries({ queryKey: historyKey(grantKey) });
}

/**
 * Mark the list stale **without fetching** — what the Watch page does after recording a watch.
 *
 * `refetchType: 'none'` because the reader is watching a video, not looking at their history: the row is
 * on the server, and the list is re-read whenever they next open the page. Fetching here would spend a
 * request to update a screen nobody is looking at, mid-playback.
 */
export function markHistoryStale(client: QueryClient, grantKey: number): void {
  void client.invalidateQueries({ queryKey: historyKey(grantKey), refetchType: 'none' });
}
